import { useEffect, useState, useRef, createContext, useContext, useCallback, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
// Card/CardContent no longer needed – banner uses custom styled container
import { X, Cookie, Settings } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { updateConsentMode, clearAdvertisingCookies, clearAnalyticsCookies } from "@/lib/consentManager";

const COOKIE_CONSENT_KEY = "chaoskitchen_cookie_consent";

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
  timestamp: string;
}

interface CookieConsentContextType {
  preferences: CookiePreferences;
  hasConsented: boolean;
  openSettings: () => void;
  updatePreferences: (prefs: Partial<CookiePreferences>) => void;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  advertising: false,
  timestamp: "",
};

const CookieConsentContext = createContext<CookieConsentContextType>({
  preferences: defaultPreferences,
  hasConsented: false,
  openSettings: () => {},
  updatePreferences: () => {},
});

export const useCookieConsent = () => {
  return useContext(CookieConsentContext);
};

export const getCookiePreferences = (): CookiePreferences | null => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) {
      const parsed = JSON.parse(consent);
      if (parsed.analytics === undefined) {
        parsed.analytics = false;
      }
      return parsed;
    }
  } catch (e) {
    console.error("Error reading cookie preferences:", e);
  }
  return null;
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider = ({ children }: CookieConsentProviderProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isOverlayRoute = location.pathname.startsWith('/overlay/');
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  // Check if user is on a paid tier (should not see ad-related consent prompts)
  const { user } = useCurrentUser();

  const { data: subscription } = useQuery({
    queryKey: ['subscription-for-consent', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('user_subscriptions')
        .select('tier, subscription_status, trial_tier, trial_end_date')
        .eq('user_id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id && !isOverlayRoute,
    staleTime: 60000,
  });

  // Check family membership for paid user detection
  const { data: familyMembership } = useQuery({
    queryKey: ['family-membership-consent', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('family_members')
        .select('id, status')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id && !isOverlayRoute,
    staleTime: 60000,
  });

  // Calculate if user is on paid tier (including trials and family membership)
  const isPaidUser = (() => {
    // Family members inherit Chef Master benefits
    if (familyMembership) return true;

    if (!subscription) return false;
    
    // Check for active paid subscription
    if (subscription.tier !== 'free' && 
        (subscription.subscription_status === 'active' || subscription.subscription_status === 'trialing')) {
      return true;
    }
    
    // Check for active trial
    if (subscription.trial_tier && subscription.trial_end_date) {
      const trialEnd = new Date(subscription.trial_end_date);
      if (trialEnd > new Date()) {
        return true;
      }
    }
    
    return false;
  })();

  useEffect(() => {
    // Paid users never need to see ad consent banner
    if (isPaidUser) {
      setIsVisible(false);
      return;
    }

    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
      setHasConsented(false);
    } else {
      try {
        const parsed = JSON.parse(consent);
        if (parsed.analytics === undefined) {
          parsed.analytics = false;
        }
        setPreferences(parsed);
        setHasConsented(true);
      } catch (e) {
        setIsVisible(true);
        setHasConsented(false);
      }
    }
  }, [isPaidUser]);

  const previousPrefsRef = useRef<CookiePreferences>(preferences);

  const savePreferences = useCallback(async (prefs: CookiePreferences) => {
    const prefsWithTimestamp = {
      ...prefs,
      timestamp: new Date().toISOString(),
    };
    
    const oldPrefs = previousPrefsRef.current;
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefsWithTimestamp));
    setPreferences(prefsWithTimestamp);
    setHasConsented(true);
    setIsVisible(false);
    setShowSettings(false);
    
    // Centralized consent mode update
    updateConsentMode(prefs.analytics, prefs.advertising);
    
    // GDPR: actively clear cookies when consent is revoked
    if (oldPrefs.advertising && !prefs.advertising) {
      clearAdvertisingCookies();
    }
    if (oldPrefs.analytics && !prefs.analytics) {
      clearAnalyticsCookies();
    }
    
    previousPrefsRef.current = prefs;
    
    // Sync consent preferences to backend for logged-in users
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({
            ad_consent_preferences: JSON.parse(JSON.stringify(prefs)),
            ad_consent_updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Failed to sync consent to backend:', error);
    }
    
    if (!prefs.advertising) {
      console.log("Advertising cookies rejected - personalized ads disabled, contextual ads will be shown instead");
    }
    if (!prefs.analytics) {
      console.log("Analytics cookies rejected - analytics will not load");
    }
  }, []);

  const acceptAll = useCallback(() => {
    savePreferences({
      necessary: true,
      analytics: true,
      advertising: true,
      timestamp: new Date().toISOString(),
    });
  }, [savePreferences]);

  const rejectOptional = useCallback(() => {
    savePreferences({
      necessary: true,
      analytics: false,
      advertising: false,
      timestamp: new Date().toISOString(),
    });
  }, [savePreferences]);

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const updatePreferences = useCallback((newPrefs: Partial<CookiePreferences>) => {
    setPreferences(prev => ({ ...prev, ...newPrefs }));
  }, []);

  const saveCustomPreferences = useCallback(() => {
    savePreferences(preferences);
  }, [preferences, savePreferences]);

  const contextValue: CookieConsentContextType = {
    preferences,
    hasConsented,
    openSettings,
    updatePreferences,
  };

  // Overlay routes: no banner, no dialog, no tracking — skip everything
  if (isOverlayRoute) {
    return (
      <CookieConsentContext.Provider value={contextValue}>
        {children}
      </CookieConsentContext.Provider>
    );
  }

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}

      {isVisible && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 pointer-events-none animate-fade-in">
          <div className="max-w-4xl mx-auto pointer-events-auto rounded-2xl border border-primary/10 bg-surface-overlay/98 backdrop-blur-xl shadow-[0_-8px_40px_rgba(0,0,0,0.3)] overflow-hidden">
            {/* Gradient accent strip */}
            <div className="h-[2px] bg-gradient-to-r from-primary/60 via-chaos-glow/40 to-primary/60" />
            
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                {/* Glowing cookie icon */}
                <div className="flex-shrink-0 bg-primary/10 rounded-full p-3 ring-2 ring-primary/20">
                  <Cookie className="h-6 w-6 text-primary" />
                </div>
                
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-lg font-heading font-bold text-foreground tracking-tight">
                      {t('cookieConsent.title')}
                    </h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={rejectOptional}
                      className="flex-shrink-0 h-8 w-8 rounded-full hover:bg-surface-elevated"
                      data-testid="button-cookie-close"
                      aria-label="Close cookie banner"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t('cookieConsent.description')}{" "}
                    <a href="/cookie-policy" className="text-primary hover:underline font-medium">
                      {t('cookieConsent.cookiePolicy')}
                    </a>
                  </p>

                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button
                      onClick={acceptAll}
                      className="flex-1 sm:flex-initial shadow-[0_0_20px_hsl(var(--primary)/0.3)] hover:shadow-[0_0_28px_hsl(var(--primary)/0.4)] hover:scale-[1.02] transition-all duration-200"
                      data-testid="button-cookie-accept-all"
                    >
                      {t('cookieConsent.acceptAll')}
                    </Button>
                    
                    <Button
                      onClick={rejectOptional}
                      variant="outline"
                      className="flex-1 sm:flex-initial hover:border-primary/40 transition-colors duration-200"
                      data-testid="button-cookie-reject"
                    >
                      {t('cookieConsent.rejectOptional')}
                    </Button>

                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="flex-1 sm:flex-initial hover:bg-surface-elevated transition-colors duration-200"
                      data-testid="button-cookie-customize"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      {t('cookieConsent.customize')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl bg-surface-overlay border-primary/10 overflow-hidden p-0">
          {/* Gradient accent strip */}
          <div className="h-[2px] bg-gradient-to-r from-primary/60 via-chaos-glow/40 to-primary/60" />
          
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 font-heading">
                <div className="bg-primary/10 rounded-full p-2 ring-2 ring-primary/20">
                  <Cookie className="h-5 w-5 text-primary" />
                </div>
                {t('cookieConsent.settingsTitle')}
              </DialogTitle>
              <DialogDescription>
                {t('cookieConsent.settingsDescription')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Necessary cookies */}
              <div className="bg-surface/50 rounded-xl p-4 border border-border/50 hover:bg-surface-elevated/50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="space-y-1 flex-1">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                      {t('cookieConsent.necessaryCookies')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('cookieConsent.necessaryDescription')}
                    </p>
                  </div>
                  <Switch
                    checked={true}
                    disabled
                    className="ml-4"
                    data-testid="switch-cookie-necessary"
                  />
                </div>
              </div>

              {/* Analytics cookies */}
              <div className="bg-surface/50 rounded-xl p-4 border border-border/50 hover:bg-surface-elevated/50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="space-y-1 flex-1">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                      {t('cookieConsent.analyticsCookies')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('cookieConsent.analyticsDescription')}
                    </p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) =>
                      setPreferences({ ...preferences, analytics: checked })
                    }
                    className="ml-4"
                    data-testid="switch-cookie-analytics"
                  />
                </div>
              </div>

              {/* Advertising cookies */}
              <div className="bg-surface/50 rounded-xl p-4 border border-border/50 hover:bg-surface-elevated/50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="space-y-1 flex-1">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                      {t('cookieConsent.advertisingCookies')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('cookieConsent.advertisingDescription')}
                    </p>
                    <p className="text-xs text-muted-foreground/80 mt-1">
                      {t('cookieConsent.advertisingPurposes')}
                    </p>
                    <a
                      href="https://policies.google.com/technologies/partner-sites"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline inline-block mt-1 font-medium"
                    >
                      {t('cookieConsent.advertisingPartnersLink')}
                    </a>
                  </div>
                  <Switch
                    checked={preferences.advertising}
                    onCheckedChange={(checked) =>
                      setPreferences({ ...preferences, advertising: checked })
                    }
                    className="ml-4"
                    data-testid="switch-cookie-advertising"
                  />
                </div>
              </div>

              <p className="text-xs text-muted-foreground/70 pt-2">
                {t('cookieConsent.withdrawConsent')}
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)} className="hover:border-primary/40 transition-colors duration-200" data-testid="button-cookie-cancel">
                {t('common.cancel')}
              </Button>
              <Button onClick={saveCustomPreferences} className="shadow-[0_0_20px_hsl(var(--primary)/0.3)] hover:shadow-[0_0_28px_hsl(var(--primary)/0.4)] hover:scale-[1.02] transition-all duration-200" data-testid="button-cookie-save">
                {t('cookieConsent.savePreferences')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </CookieConsentContext.Provider>
  );
};

export const CookieConsent = CookieConsentProvider;
